import { forwardRef, useImperativeHandle, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';

import { create, update } from 'apis/organizations.api';
import { createOrganizationValidationSchema } from 'features/Organization/validation';
import { Select, TextareaInput, TextInput } from 'components/FormControl';
import Loading from 'components/Loading';
import { TOAST } from 'constant';
import { toast } from 'layout';
import { mapError } from 'utils/func';

const formOptions = { resolver: yupResolver(createOrganizationValidationSchema) };

const Form = forwardRef(({ refetch }, ref) => {
  // #region Data
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [organizationControl, setOrganizationControl] = useState(null);

  const {
    handleSubmit,
    control,
    trigger,
    reset,
    formState: { errors, dirtyFields, isDirty },
  } = useForm({ ...formOptions, mode: 'onChange' });

  const { mutate: mutateCreate, isLoading: isLoadingCreate } = useMutation(create);
  const { mutate: mutateUpdate, isLoading: isLoadingUpdate } = useMutation(update);

  // #endregion

  // #region Event
  useImperativeHandle(
    ref,
    () => ({
      open: (_organizationControl) => {
        setOrganizationControl(_organizationControl);
        setVisible(true);
        if (_organizationControl.mode === 'CREATE') {
          reset({});
        } else if (_organizationControl.mode === 'UPDATE') {
          reset(_organizationControl.value);
        }
      },
    }),
    []
  );

  const onSubmit = (data) => {
    if (organizationControl.mode === 'CREATE') {
      mutateCreate(data, {
        onSuccess: () => {
          toast(TOAST.SUCCESS, t('organization.create.createSuccess'));
          reset();
          refetch();
          setVisible(false);
        },
        onError: (error) => {
          toast(TOAST.ERROR, mapError(t, error));
        },
      });
    } else {
      const newData = Object.keys(dirtyFields)?.reduce((resultObj, current) => {
        // eslint-disable-next-line no-param-reassign
        resultObj[current] = data[current];
        return resultObj;
      }, {});

      mutateUpdate(
        { id: data?.id, data: newData },
        {
          onSuccess: () => {
            toast(TOAST.SUCCESS, t('organization.update.updateSuccess'));
            refetch();
            setVisible(false);
          },
          onError: (error) => {
            toast(TOAST.ERROR, mapError(t, error));
          },
        }
      );
    }
  };

  const checkFormValid = async () => {
    const isValid = await trigger();
    if (!isValid) toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
  };

  const checkKeyDown = (e) => {
    if (e.code === 'Enter') e.preventDefault();
  };
  // #endregion

  return (
    <Dialog
      header={
        organizationControl?.mode === 'CREATE'
          ? t('organization.create.title')
          : t('organization.update.title')
      }
      visible={visible}
      style={{ maxWidth: '700px' }}
      draggable={false}
      onHide={() => setVisible(false)}
    >
      <div className="relative">
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={checkKeyDown}
          className="p-fluid flex flex-column"
          autoComplete="off"
        >
          <div className="grid">
            <div className="col-12">
              <TextInput
                label={t('organization.name')}
                name="name"
                autoFocus
                control={control}
                errors={errors}
                isRequired
              />
            </div>

            <div className="col-12">
              <TextareaInput
                label={t('organization.englishName')}
                name="englishName"
                control={control}
                errors={errors}
                isRequired
              />
            </div>

            <div className="col-6">
              <TextareaInput
                label={t('organization.chief')}
                name="chief"
                control={control}
                errors={errors}
                isRequired
              />
            </div>
            <div className="col-6">
              <TextareaInput
                label={t('organization.email')}
                name="email"
                control={control}
                errors={errors}
              />
            </div>

            <div className="col-6">
              <TextareaInput
                label={t('organization.phone')}
                name="phone"
                control={control}
                errors={errors}
              />
            </div>
            <div className="col-6">
              <TextareaInput
                label={t('organization.fax')}
                name="fax"
                control={control}
                errors={errors}
              />
            </div>

            <div className="col-12">
              <TextareaInput
                label={t('organization.address')}
                name="address"
                control={control}
                errors={errors}
              />
            </div>

            <div className="col-6">
              <TextareaInput
                label={t('organization.bankAccount')}
                name="bankAccount"
                control={control}
                errors={errors}
              />
            </div>
            <div className="col-6">
              <TextareaInput
                label={t('organization.bank')}
                name="bank"
                control={control}
                errors={errors}
              />
            </div>

            <div className="col-6">
              <TextareaInput
                label={t('organization.code')}
                name="code"
                control={control}
                errors={errors}
                disabled={organizationControl?.value?.code === 'vnu'}
              />
            </div>
            <div className="col-6">
              <Select
                label={t('organization.inVnuHcm')}
                name="inVnuHcm"
                options={[
                  { name: t('organization.yes'), value: 1 },
                  { name: t('organization.no'), value: 0 },
                ]}
                control={control}
                errors={errors}
                inputField="value"
                isRequired
              />
            </div>
          </div>

          <div className="grid mt-2">
            <div className="col-6">
              <Button
                label={t('organization.cancel')}
                type="button"
                severity="danger"
                onClick={() => {
                  reset();
                  setVisible(false);
                }}
              />
            </div>
            <div className="col-6">
              <Button
                label={
                  organizationControl?.mode === 'CREATE'
                    ? t('organization.add')
                    : t('organization.save')
                }
                icon="pi pi-pencil"
                className="bg-cyan-400 border-cyan-400 hover:bg-cyan-500"
                type="submit"
                onClick={checkFormValid}
                disabled={!isDirty || !(Object.keys(dirtyFields)?.length > 0)}
              />
            </div>
          </div>
        </form>
        {(isLoadingCreate || isLoadingUpdate) && <Loading />}
      </div>
    </Dialog>
  );
});

export default Form;

Form.propTypes = {
  refetch: PropTypes.func.isRequired,
};
